import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Heart, ShoppingBag, Trash2, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WishlistPage() {
  const { user, token } = useAuth();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadWishlist = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/wishlist`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(res.data.products || []);
    } catch { /* empty */ }
    setLoading(false);
  }, [token]);

  useEffect(() => { loadWishlist(); }, [loadWishlist]);

  const removeItem = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch { /* empty */ }
  };

  if (!user) return (
    <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center">
      <div className="text-center space-y-4">
        <Heart className="h-16 w-16 text-[var(--dark)]/15 mx-auto" />
        <h2 className="text-2xl font-bold text-[var(--dark)]">Lista de Deseos</h2>
        <p className="text-sm text-[var(--dark)]/40">Inicia sesion para ver tus favoritos</p>
        <Link to="/auth"><Button className="bg-[var(--violet)] text-white rounded-xl" data-testid="wishlist-login-btn">Iniciar Sesion</Button></Link>
      </div>
    </div>
  );

  if (loading) return <div className="min-h-screen bg-[var(--cream)] flex items-center justify-center"><div className="w-8 h-8 border-2 border-[var(--violet)] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="wishlist-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link to="/tienda" className="inline-flex items-center gap-2 text-xs text-[var(--dark)]/40 hover:text-[var(--violet)] mb-6">
          <ArrowLeft className="h-3.5 w-3.5" /> Seguir comprando
        </Link>
        <h1 className="text-3xl font-bold text-[var(--dark)] mb-2">Lista de Deseos</h1>
        <p className="text-sm text-[var(--dark)]/40 mb-8">{products.length} {products.length === 1 ? 'producto' : 'productos'} guardados</p>

        {products.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 text-[var(--dark)]/10 mx-auto mb-4" />
            <h2 className="text-lg font-bold text-[var(--dark)] mb-2">Tu lista esta vacia</h2>
            <p className="text-sm text-[var(--dark)]/40 mb-6">Explora nuestros productos y agrega tus favoritos</p>
            <Link to="/tienda"><Button className="bg-[var(--violet)] text-white rounded-xl px-6" data-testid="wishlist-shop-btn">Explorar Tienda</Button></Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map(p => {
              const hasDiscount = p.discount_price && p.discount_price < p.price;
              const price = hasDiscount ? p.discount_price : p.price;
              const outOfStock = (p.stock || 0) <= 0;
              return (
                <div key={p.id} className="bg-white rounded-2xl overflow-hidden border border-[var(--sage)]/15 group" data-testid={`wishlist-item-${p.id}`}>
                  <Link to={`/producto/${p.id}`} className="block relative aspect-[4/5] overflow-hidden bg-[var(--cream)]">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    {outOfStock && <Badge className="absolute top-3 left-3 bg-red-500 text-white border-0 text-[10px] font-bold">Agotado</Badge>}
                    {hasDiscount && !outOfStock && <Badge className="absolute top-3 left-3 bg-[var(--violet)] text-white border-0 text-[10px] font-bold">-{Math.round((1 - p.discount_price / p.price) * 100)}%</Badge>}
                  </Link>
                  <div className="p-4 space-y-2">
                    <p className="text-[10px] text-[var(--violet)] font-semibold tracking-wider uppercase">{p.brand}</p>
                    <Link to={`/producto/${p.id}`}><h3 className="text-xs font-semibold text-[var(--dark)] line-clamp-2 hover:text-[var(--violet)]">{p.name}</h3></Link>
                    <div className="flex items-baseline gap-1.5">
                      {hasDiscount && <span className="text-[10px] text-[var(--dark)]/25 line-through">${p.price?.toFixed(2)}</span>}
                      <span className={`text-sm font-bold ${hasDiscount ? 'text-[var(--violet)]' : 'text-[var(--dark)]'}`}>${price?.toFixed(2)}</span>
                    </div>
                    <div className="flex gap-2 pt-1">
                      <Button
                        size="sm"
                        className={`flex-1 text-[10px] h-8 ${outOfStock ? 'bg-gray-200 text-gray-400 cursor-not-allowed' : 'bg-[var(--dark)] hover:bg-[var(--violet)] text-white'}`}
                        onClick={() => !outOfStock && addItem(p)}
                        disabled={outOfStock}
                        data-testid={`wishlist-add-cart-${p.id}`}
                      >
                        <ShoppingBag className="h-3 w-3 mr-1" /> {outOfStock ? 'Agotado' : 'Agregar al carrito'}
                      </Button>
                      <Button size="icon" variant="outline" className="h-8 w-8 text-red-400 hover:text-red-600 hover:bg-red-50 border-red-200" onClick={() => removeItem(p.id)} data-testid={`wishlist-remove-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
